import React from "react";
import { NavDropdown } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
// @ts-ignore CSS is loaded by the bundler; no CSS module declaration is configured.
import "./navLinks.css";

interface NavItemConfig {
  label: string;
  to: string;
  icon: string;
  iconVariant?: string;
}

interface NavGroupConfig {
  id: string;
  title: string;
  icon: string;
  header: string;
  items: NavItemConfig[];
}

const primaryNavGroups: NavGroupConfig[] = [
  {
    id: "admin-nav-students",
    title: "Alumnos",
    icon: "fas fa-user-graduate",
    header: "Gestión de Alumnos",
    items: [
      {
        label: "Ver todos los alumnos",
        to: "/students/viewAll",
        icon: "fas fa-users",
      },
      {
        label: "Agregar un alumno nuevo",
        to: "/students/create",
        icon: "fas fa-user-plus",
        iconVariant: "rc-nav-item-icon-success",
      },
      {
        label: "Importar alumnos desde CSV",
        to: "/students/import-csv",
        icon: "fas fa-file-csv",
        iconVariant: "rc-nav-item-icon-success",
      },
      {
        label: "Subscripciones",
        to: "/students/subscriptions",
        icon: "fas fa-id-card",
      },
      {
        label: "Certificados",
        to: "/certificates",
        icon: "fas fa-certificate",
        iconVariant: "rc-nav-item-icon-accent",
      },
    ],
  },
  {
    id: "admin-nav-payments",
    title: "Pagos",
    icon: "fas fa-file-invoice-dollar",
    header: "Facturación y Cobros",
    items: [
      {
        label: "Ver todos los pagos",
        to: "/bills/viewAll",
        icon: "fas fa-file-invoice",
      },
      {
        label: "Planes",
        to: "/bills/payment-plan",
        icon: "fas fa-layer-group",
      },
      {
        label: "Pagos en MercadoPago",
        to: "/bills/paymentsMp",
        icon: "fas fa-credit-card",
      },
      {
        label: "Links de MercadoPago",
        to: "/bills/createLinkPayment",
        icon: "fas fa-link",
      },
      {
        label: "Cupones",
        to: "/bills/coupons",
        icon: "fas fa-gift",
        iconVariant: "rc-nav-item-icon-warning",
      },
    ],
  },
  {
    id: "admin-nav-commissions",
    title: "Comisiones",
    icon: "fas fa-laptop-code",
    header: "Académico",
    items: [
      {
        label: "Comisiones",
        to: "/commission/",
        icon: "fas fa-laptop",
      },
      {
        label: "Cursos",
        to: "/cursos/",
        icon: "fas fa-book",
      },
      {
        label: "Carreras",
        to: "/carreras/",
        icon: "fas fa-graduation-cap",
      },
      {
        label: "Mentores",
        to: "/teachers",
        icon: "fas fa-chalkboard-teacher",
      },
    ],
  },
];

const secondaryNavGroups: NavGroupConfig[] = [
  {
    id: "admin-nav-collections",
    title: "Cobranzas",
    icon: "fas fa-hand-holding-usd",
    header: "Seguimiento de Deuda",
    items: [
      {
        label: "Listado 2.0",
        to: "/debt-collection-traking",
        icon: "fas fa-list-alt",
      },
    ],
  },
  {
    id: "admin-nav-configurations",
    title: "Configuraciones",
    icon: "fas fa-sliders-h",
    header: "Ajustes y Comunicación",
    items: [
      {
        label: "Objetivos Ventas",
        to: "/configurations/sellTargets",
        icon: "fas fa-bullseye",
        iconVariant: "rc-nav-item-icon-accent",
      },
      {
        label: "Mensajes",
        to: "/communications/dashboard",
        icon: "fas fa-envelope",
      },
      {
        label: "Promociones",
        to: "/communications/promotions",
        icon: "fas fa-gift",
        iconVariant: "rc-nav-item-icon-warning",
      },
    ],
  },
];

const utilityNavGroups: NavGroupConfig[] = [
  {
    id: "admin-nav-kiosk",
    title: "RollingKiosk",
    icon: "fas fa-store",
    header: "Kiosco y rCoins",
    items: [
      {
        label: "Agregar productos",
        to: "/rollingKiosk/addProduct",
        icon: "fas fa-plus",
        iconVariant: "rc-nav-item-icon-success",
      },
      {
        label: "Controlar stock",
        to: "/rollingKiosk/viewProducts",
        icon: "fas fa-eye",
      },
      {
        label: "Registrar alumno",
        to: "/rollingKiosk/registerPhone",
        icon: "fas fa-user-plus",
      },
      {
        label: "Cargar rCoins",
        to: "/rollingKiosk/ChargeRcCoins",
        icon: "fas fa-dollar-sign",
        iconVariant: "rc-nav-item-icon-warning",
      },
    ],
  },
  {
    id: "admin-nav-statistics",
    title: "Estadísticas",
    icon: "fas fa-chart-pie",
    header: "Reportes y Métricas",
    items: [
      {
        label: "Ingresos",
        to: "/graphic?tab=incomes",
        icon: "fas fa-money-bill-wave",
        iconVariant: "rc-nav-item-icon-success",
      },
      {
        label: "Métricas de ventas",
        to: "/graphic?tab=sales-metrics",
        icon: "fas fa-chart-line",
      },
      {
        label: "Altas y bajas",
        to: "/graphic?tab=studentsCounter",
        icon: "fas fa-user-check",
      },
      {
        label: "Ciudades",
        to: "/graphic?tab=cities",
        icon: "fas fa-city",
      },
      {
        label: "Comisiones",
        to: "/graphic?tab=classrooms",
        icon: "fas fa-laptop",
      },
      {
        label: "Género",
        to: "/graphic?tab=gender",
        icon: "fas fa-venus-mars",
      },
      {
        label: "Edades",
        to: "/graphic?tab=ages",
        icon: "fas fa-birthday-cake",
      },
    ],
  },
];

export default function AdminNav() {
  const location = useLocation();
  const currentUrl = `${location.pathname}${location.search}`;

  const isRouteActive = (targetRoute: string): boolean => {
    const normalizedCurrent = currentUrl.replace(/\/+$/, "");
    const normalizedTarget = targetRoute.replace(/\/+$/, "");
    return normalizedCurrent === normalizedTarget;
  };

  const renderDropdownGroup = (group: NavGroupConfig) => {
    const hasActiveItem = group.items.some((item) => isRouteActive(item.to));

    return (
      <NavDropdown
        key={group.id}
        id={group.id}
        className={`rc-nav-dropdown ${hasActiveItem ? "rc-nav-active" : ""}`}
        title={
          <span className="d-inline-flex align-items-center">
            <i className={`${group.icon} rc-nav-title-icon mr-2`} />
            <span>{group.title}</span>
          </span>
        }
      >
        <div className="rc-nav-section-header">{group.header}</div>
        <NavDropdown.Divider className="rc-nav-menu-divider" />
        {group.items.map((item) => {
          const active = isRouteActive(item.to);
          return (
            <NavDropdown.Item
              key={item.to}
              as={Link}
              to={item.to}
              className={`rc-nav-item ${active ? "rc-nav-item-active" : ""}`}
            >
              <span
                className={`rc-nav-item-icon ${item.iconVariant ? item.iconVariant : ""}`}
              >
                <i className={item.icon} />
              </span>
              <span>{item.label}</span>
            </NavDropdown.Item>
          );
        })}
      </NavDropdown>
    );
  };

  const isSmallboxActive = isRouteActive("/smallbox/");

  return (
    <div className="rc-nav-group">
      {primaryNavGroups.map(renderDropdownGroup)}
      <Link
        className={`nav-link rc-nav-direct-link ${isSmallboxActive ? "rc-nav-active" : ""}`}
        to="/smallbox/"
      >
        <i className="fas fa-cash-register rc-nav-title-icon" />
        <span>Caja chica</span>
      </Link>
      {secondaryNavGroups.map(renderDropdownGroup)}
      <div className="rc-nav-separator" aria-hidden="true" />
      {utilityNavGroups.map(renderDropdownGroup)}
    </div>
  );
}
